import type { FC, ReactNode } from 'react'

import { Mail, Phone, Plus } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { company } from '@/constants/company'
import { FAQS } from '@/constants/content'

export const FaqSection: FC = (): ReactNode => {
    return (
        <section
            id='faq'
            aria-labelledby='faq-title'
            className='border-y border-border bg-card py-24 sm:py-28'
        >
            <div className='mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-4'>
                    <div className='lg:sticky lg:top-28'>
                        <SectionHeading
                            titleId='faq-title'
                            eyebrow='Häufige Fragen'
                            title='Was Bauleitung und QS vorab wissen wollen.'
                            description='Kurze Antworten auf die Fragen, die uns vor dem ersten Projekt am häufigsten gestellt werden.'
                        />

                        <div className='mt-8 rounded-[1.5rem] border border-border bg-background/60 p-6'>
                            <p className='font-medium text-foreground'>
                                Ihre Frage ist nicht dabei?
                            </p>

                            <p className='mt-1 text-pretty text-[0.95rem] leading-relaxed text-muted-foreground'>
                                Rufen Sie an oder schreiben Sie uns. Sie sprechen direkt mit dem Team.
                            </p>

                            <div className='mt-4 grid gap-2.5 text-[0.95rem]'>
                                <a
                                    href={company.phoneHref}
                                    className='inline-flex items-center gap-2.5 font-medium text-foreground transition-colors hover:text-primary'
                                >
                                    <Phone className='size-4 text-primary' aria-hidden='true' />
                                    {company.phone}
                                </a>

                                <a
                                    href={`mailto:${company.email}`}
                                    className='inline-flex items-center gap-2.5 break-all font-medium text-foreground transition-colors hover:text-primary'
                                >
                                    <Mail className='size-4 shrink-0 text-primary' aria-hidden='true' />
                                    {company.email}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='divide-y divide-border border-y border-border lg:col-span-8'>
                    {FAQS.map((faq, index) => (
                        <details
                            key={faq.question}
                            name='faq'
                            open={index === 0}
                            className='group'
                        >
                            <summary className='flex cursor-pointer list-none items-start justify-between gap-6 rounded-lg py-6 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-card [&::-webkit-details-marker]:hidden'>
                                <h3 className='text-pretty text-lg font-medium leading-snug text-foreground sm:text-xl'>
                                    {faq.question}
                                </h3>

                                <span className='flex size-8 shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-[transform,background-color] duration-200 group-open:rotate-45 group-open:bg-accent group-open:text-accent-foreground'>
                                    <Plus className='size-4' aria-hidden='true' />
                                </span>
                            </summary>

                            <div className='grid gap-4 pb-7 pr-2 text-pretty leading-relaxed text-muted-foreground sm:pr-14'>
                                {faq.answer.map((paragraph) => (
                                    <p key={paragraph}>
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    )
}
